(() => {
  if (window.htsVariantSelectionInstalled) return;
  window.htsVariantSelectionInstalled = true;

  class VariantSelection {
    constructor(root) {
      this.root = root;
      this.info = root.closest('product-info');
      this.section = root.dataset.section;
      this.version = 0;
      this.state = 'loading';
      this.desiredId = '';
      this.confirmedId = '';
      this.info.rcpSelection = this;
      root.addEventListener('click', (event) => this.click(event));
      this.info.addEventListener('submit', (event) => {
        if (!event.target.matches('form[action*="/cart/add"]')) return;
        if (this.canSubmit(event.target)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.state !== 'loading') this.fail(this.version);
      }, true);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.sectionId !== this.section || !root.isConnected) return;
        const version = this.version;
        queueMicrotask(() => {
          if (version !== this.version || !root.isConnected) return;
          this.confirm(event.data.variant, version);
        });
      });
      const variant = this.current();
      if (variant) {
        this.desiredId = String(variant.id);
        this.showPlan(variant.option1);
        this.mark(this.desiredId);
        this.confirm(variant, this.version);
      } else {
        this.showPlan(root.querySelector('.rcp-plan')?.dataset.plan);
        this.fail(this.version);
      }
    }

    current() {
      const json = this.info.querySelector('variant-selects [data-selected-variant]');
      try { return json ? JSON.parse(json.textContent) : null; } catch (_) { return null; }
    }

    showPlan(plan) {
      this.root.querySelectorAll('.rcp-plan').forEach((button) => {
        button.setAttribute('aria-checked', String(button.dataset.plan === plan));
      });
      this.root.querySelectorAll('[data-plan-days]').forEach((group) => {
        group.hidden = group.dataset.planDays !== plan;
      });
    }

    mark(id) {
      this.root.querySelectorAll('.rcp-day').forEach((button) => {
        button.setAttribute('aria-checked', String(button.dataset.variant === String(id)));
      });
    }

    matches() {
      const variant = this.current();
      const summary = this.info.querySelector('[data-rd-selected]');
      const selected = this.root.querySelector('.rcp-day[aria-checked="true"]');
      const plan = this.root.querySelector('.rcp-plan[aria-checked="true"]');
      return variant && String(variant.id) === this.desiredId &&
        selected?.dataset.variant === this.desiredId && plan?.dataset.plan === variant.option1 &&
        (!summary || summary.dataset.variantId === this.desiredId);
    }

    canSubmit(form) {
      if (this.state !== 'ready' || this.confirmedId !== this.desiredId || !this.matches()) return false;
      if (!this.current().available) return false;
      const ids = new FormData(form).getAll('id');
      return ids.length === 1 && String(ids[0]) === this.desiredId;
    }

    setState(state) {
      this.state = state;
      this.root.dataset.rcpState = state;
      this.info.dataset.rcpState = state;
      this.root.setAttribute('aria-busy', String(state === 'loading'));
      this.root.querySelector('[data-rcp-loading]').hidden = state !== 'loading';
      this.root.querySelector('[data-rcp-error]').hidden = state !== 'error';
      const disabled = state !== 'ready';
      this.info.productForm?.toggleSubmitButton(disabled,
        state === 'unavailable' ? window.variantStrings.soldOut : undefined);
      const button = this.info.productForm?.querySelector('[type="submit"]');
      if (disabled) button?.setAttribute('aria-disabled', 'true');
      else button?.removeAttribute('aria-disabled');
      this.info.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout').forEach((payment) => {
        payment.inert = disabled;
      });
    }

    confirm(variant, version) {
      if (version !== this.version) return;
      if (!variant || String(variant.id) !== this.desiredId || !this.matches()) {
        this.fail(version);
        return;
      }
      const forms = [...this.info.querySelectorAll('product-form form[action*="/cart/add"]')];
      if (!forms.length || forms.some((form) => {
        const ids = new FormData(form).getAll('id');
        return ids.length !== 1 || String(ids[0]) !== this.desiredId;
      })) {
        this.fail(version);
        return;
      }
      this.confirmedId = this.desiredId;
      this.showPlan(variant.option1);
      this.mark(variant.id);
      this.setState(variant.available ? 'ready' : 'unavailable');
    }

    fail(version) {
      if (version !== this.version) return;
      this.confirmedId = '';
      this.setState('error');
      this.info.updateVariantInputs('');
    }

    click(event) {
      const plan = event.target.closest('.rcp-plan');
      if (plan) {
        const days = this.root.querySelector('.rcp-day[aria-checked="true"]')?.dataset.days;
        const group = [...this.root.querySelectorAll('[data-plan-days]')]
          .find((item) => item.dataset.planDays === plan.dataset.plan);
        const buttons = group ? [...group.querySelectorAll('.rcp-day:not([disabled])')] : [];
        const target = buttons.find((button) => button.dataset.days === days) || buttons[0];
        if (target) this.pick(target);
        return;
      }
      const day = event.target.closest('.rcp-day');
      if (day && !day.disabled) this.pick(day);
      if (event.target.closest('[data-rcp-retry]')) {
        const target = [...this.root.querySelectorAll('.rcp-day')]
          .find((button) => button.dataset.variant === this.desiredId);
        if (target && !target.disabled) this.pick(target);
      }
    }

    pick(button) {
      const id = button.dataset.variant;
      if (this.state === 'ready' && id === this.desiredId && this.matches()) return;
      const version = ++this.version;
      this.desiredId = id;
      this.confirmedId = '';
      this.showPlan(button.dataset.plan);
      this.mark(id);
      this.info.resetProductFormState();
      this.setState('loading');
      this.info.updateVariantInputs('');

      const url = new URL(this.info.dataset.url, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('variant', id);
      this.root.querySelector('[data-rcp-reload]').href = url.href;
      url.searchParams.set('section_id', this.section);

      this.info.renderProductInfo({
        requestUrl: url.href,
        targetId: button.id,
        timeoutMs: 12000,
        onError: () => this.fail(version),
        callback: (html) => {
          if (version !== this.version || !this.root.isConnected) return;
          const variant = this.info.getSelectedVariant(html);
          const summary = html.getElementById(`RdSelected-${this.section}`);
          if (!html.getElementById(this.info.id) || !variant || String(variant.id) !== id ||
              (summary && summary.dataset.variantId !== id)) {
            this.fail(version);
            return;
          }
          this.info.handleUpdateProductInfo(this.info.dataset.url)(html);
        },
      });
    }
  }

  function init() {
    Promise.all([customElements.whenDefined('product-info'), customElements.whenDefined('product-form')]).then(() => {
      document.querySelectorAll('[data-rcp-controller="variant-id-v1"]').forEach((root) => {
        const info = root.closest('product-info');
        if (!info || info.rcpSelection?.root === root) return;
        info.rcpSelection?.unsubscribe?.();
        new VariantSelection(root);
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('product-info:loaded', init);
  document.addEventListener('shopify:section:load', init);
})();
